import api from './api';
import tournamentService from './tournamentService';
import { t } from '../i18n';

// ============================================
// PlacesService (2026-09-04) – Standort-Landing-Pages
// „Darten in <Stadt>“: GET /api/places, GET /api/places/:slug(?category=)
// Alle Felder defensiv nullable geparst (Backend-Felder sind additiv).
// ============================================

export type PlaceCategoryKey = 'club' | 'pub' | 'shop' | 'tournament' | 'series' | 'league' | 'association';
export type PlaceKind = 'city' | 'region' | 'country';

export interface PlaceInfo {
  kind: PlaceKind;
  name: string;
  slug: string;
  cc: string | null;
  country: string | null;
  lat: number | null;
  lng: number | null;
  population: number | null;
  stateName: string | null;
  stateSlug: string | null;
  regionLabel: string | null;
}

/** Fakten je Eintrag (alle nullable — kommen additiv vom Backend) */
export interface PlaceEntryFacts {
  steeldart: boolean | null;
  edart: boolean | null;
  openTraining: boolean | null;
  leagues: number | null;
  membershipInfo: boolean | null;
  localDartTesting: boolean | null;
  localPointChange: boolean | null;
  onlineShop: boolean | null;
  rentable: boolean | null;
  gameMode: string | null;
  boards: number | null;
  entryFee: number | null;
  startTime: string | null;
  weekday: number | null;
  intervalWeeks: number | null;
}

export interface PlaceEntry {
  id: string;
  type: PlaceCategoryKey;
  name: string;
  slug: string;
  lat: number;
  lng: number;
  addressLabel: string | null;
  cityLabel: string | null;
  image: string | null;
  nextDate: number | null;
  dateLabel: string | null;
  path: string;
  distanceKm: number | null;
  /** true = liegt direkt im Ort (Adress-Stadt oder Stadt-Radius), false = Umkreis, null = unbekannt (Region) */
  inCity: boolean | null;
  facts: PlaceEntryFacts;
  nearestPlace: { name: string; slug: string } | null;
}

export interface PlaceGuide {
  heading: string;
  paragraphs: string[];
}

export interface DistrictGroup {
  name: string;
  count: number;
  entryIds: string[];
}

export interface PlaceFaq {
  question: string;
  answer: string;
}

export interface PlaceLink {
  name: string;
  slug: string | null;
  path: string;
  distanceKm: number | null;
  population: number | null;
  resultCount: number | null;
  cityState?: boolean;
}

export interface PlaceCategoryLink {
  key: PlaceCategoryKey;
  label: string;
  path: string;
  count: number;
  color: string | null;
}

export interface Breadcrumb {
  name: string;
  path: string;
}

export interface PlacePage {
  place: PlaceInfo;
  category: { key: PlaceCategoryKey; label: string; singular: string; color: string | null } | null;
  radiusKm: number | null;
  total: number;
  counts: Record<PlaceCategoryKey, number>;
  inCityCount: number | null;
  aroundCount: number | null;
  indexable: boolean;
  title: string;
  heading: string;
  description: string;
  intro: string;
  guide: PlaceGuide | null;
  groups: Partial<Record<PlaceCategoryKey, PlaceEntry[]>>;
  entries: PlaceEntry[];
  districtGroups: DistrictGroup[];
  dataAsOf: string | null;
  faq: PlaceFaq[];
  nearbyPlaces: PlaceLink[];
  regionCities: PlaceLink[];
  regions: PlaceLink[];
  categoryLinks: PlaceCategoryLink[];
  breadcrumbs: Breadcrumb[];
  canonicalPath: string;
}

export interface PlacesIndexRegion extends PlaceLink {
  cityCount: number;
  cities: PlaceLink[];
}

export interface PlacesIndexCountry {
  name: string;
  slug: string;
  cc: string | null;
  path: string;
  regions: PlacesIndexRegion[];
}

export interface PlacesIndex {
  totalPlaces: number;
  indexablePlaces: number;
  totalListings: number;
  countries: PlacesIndexCountry[];
  topCities: PlaceLink[];
  canonicalPath: string;
}

/**
 * Orts-Verlinkung einer Detailseite (additiv, 2026-09-20): Hub „Darten in <Stadt>“
 * + passende Kategorie-Seite (z. B. „Dartvereine in Berlin“). null = Backend hat
 * (noch) keinen Ort zugeordnet.
 */
export interface PlaceLinks {
  label: string;
  hubPath: string;
  categoryPath: string | null;
  categoryLabel: string | null;
}

/** Eintrag von GET /api/places/top (Startseite/Footer „Darts in deiner Stadt“) */
export interface TopPlace {
  name: string;
  slug: string;
  path: string;
  population: number | null;
  total: number | null;
}

export const PLACE_CATEGORY_KEYS: PlaceCategoryKey[] = ['club', 'pub', 'shop', 'tournament', 'series', 'league', 'association'];

// ---------- defensive Parser ----------

const str = (v: any): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);
const num = (v: any): number | null => {
  if (typeof v === 'number' && isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && isFinite(Number(v))) return Number(v);
  return null;
};
const arr = <T,>(v: any, map: (x: any) => T | null): T[] =>
  Array.isArray(v) ? v.map(map).filter((x): x is T => x != null) : [];

const isCategoryKey = (v: any): v is PlaceCategoryKey => PLACE_CATEGORY_KEYS.includes(v);
const bool = (v: any): boolean | null => (v === true ? true : v === false ? false : null);

const parseFacts = (raw: any): PlaceEntryFacts => ({
  steeldart: bool(raw?.steeldart),
  edart: bool(raw?.edart),
  openTraining: bool(raw?.openTraining),
  leagues: num(raw?.leagues),
  membershipInfo: bool(raw?.membershipInfo),
  localDartTesting: bool(raw?.localDartTesting),
  localPointChange: bool(raw?.localPointChange),
  onlineShop: bool(raw?.onlineShop),
  rentable: bool(raw?.rentable),
  gameMode: str(raw?.gameMode),
  boards: num(raw?.boards),
  entryFee: num(raw?.entryFee),
  startTime: str(raw?.startTime),
  weekday: num(raw?.weekday),
  intervalWeeks: num(raw?.intervalWeeks),
});

export const parsePlaceEntry = (raw: any): PlaceEntry | null => {
  if (!raw || typeof raw !== 'object') return null;
  const name = str(raw.name);
  const path = str(raw.path);
  const lat = num(raw.lat);
  const lng = num(raw.lng);
  if (!name || !path || lat === null || lng === null || !isCategoryKey(raw.type)) return null;
  return {
    id: str(raw.id) ?? `${raw.type}-${str(raw.slug) ?? name}`,
    type: raw.type,
    name,
    slug: str(raw.slug) ?? '',
    lat,
    lng,
    addressLabel: str(raw.addressLabel),
    cityLabel: str(raw.cityLabel),
    image: str(raw.image),
    nextDate: num(raw.nextDate),
    dateLabel: str(raw.dateLabel),
    path,
    distanceKm: num(raw.distanceKm),
    inCity: bool(raw.inCity),
    facts: parseFacts(raw.facts),
    nearestPlace: raw.nearestPlace && str(raw.nearestPlace.name) && str(raw.nearestPlace.slug)
      ? { name: str(raw.nearestPlace.name)!, slug: str(raw.nearestPlace.slug)! }
      : null,
  };
};

/** placeLinks einer Detailantwort: fehlt/kaputt → null (Sektion wird dann nicht gerendert) */
export const parsePlaceLinks = (raw: any): PlaceLinks | null => {
  if (!raw || typeof raw !== 'object') return null;
  const label = str(raw.label);
  const hubPath = str(raw.hubPath);
  if (!label || !hubPath || !hubPath.startsWith('/')) return null;
  const categoryPath = str(raw.categoryPath);
  return {
    label,
    hubPath,
    categoryPath: categoryPath && categoryPath.startsWith('/') ? categoryPath : null,
    categoryLabel: str(raw.categoryLabel),
  };
};

/** Top-Ort: Name + Slug Pflicht, Pfad fällt auf /darten-in/<slug> zurück */
export const parseTopPlace = (raw: any): TopPlace | null => {
  if (!raw || typeof raw !== 'object') return null;
  const name = str(raw.name);
  const slug = str(raw.slug);
  if (!name || !slug) return null;
  const path = str(raw.path);
  return {
    name,
    slug,
    path: path && path.startsWith('/') ? path : `/darten-in/${slug}`,
    population: num(raw.population),
    total: num(raw.total),
  };
};

/** Antwort von /api/places/top: { places: [...] } oder direkt ein Array; alles andere → [] */
export const parseTopPlaces = (raw: any): TopPlace[] => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.places) ? raw.places : [];
  const seen = new Set<string>();
  return arr(list, parseTopPlace).filter((p) => {
    if (seen.has(p.slug)) return false;
    seen.add(p.slug);
    return true;
  });
};

export const parsePlaceLink = (raw: any): PlaceLink | null => {
  if (!raw || typeof raw !== 'object') return null;
  const name = str(raw.name);
  const path = str(raw.path);
  if (!name || !path) return null;
  return {
    name,
    slug: str(raw.slug),
    path,
    distanceKm: num(raw.distanceKm),
    population: num(raw.population),
    resultCount: num(raw.resultCount),
    cityState: raw.cityState === true,
  };
};

const parsePlaceInfo = (raw: any): PlaceInfo | null => {
  if (!raw || typeof raw !== 'object') return null;
  const name = str(raw.name);
  const slug = str(raw.slug);
  if (!name || !slug) return null;
  const kind: PlaceKind = raw.kind === 'region' || raw.kind === 'country' ? raw.kind : 'city';
  return {
    kind,
    name,
    slug,
    cc: str(raw.cc),
    country: str(raw.country),
    lat: num(raw.lat),
    lng: num(raw.lng),
    population: num(raw.population),
    stateName: str(raw.stateName),
    stateSlug: str(raw.stateSlug),
    regionLabel: str(raw.regionLabel),
  };
};

export const parsePlacePage = (raw: any): PlacePage | null => {
  const place = parsePlaceInfo(raw?.place);
  if (!place) return null;
  const counts = Object.fromEntries(
    PLACE_CATEGORY_KEYS.map((k) => [k, num(raw?.counts?.[k]) ?? 0]),
  ) as Record<PlaceCategoryKey, number>;
  const groups: Partial<Record<PlaceCategoryKey, PlaceEntry[]>> = {};
  if (raw?.groups && typeof raw.groups === 'object') {
    for (const key of PLACE_CATEGORY_KEYS) {
      if (Array.isArray(raw.groups[key])) groups[key] = arr(raw.groups[key], parsePlaceEntry);
    }
  }
  const category = raw?.category && isCategoryKey(raw.category.key)
    ? {
        key: raw.category.key as PlaceCategoryKey,
        label: str(raw.category.label) ?? '',
        singular: str(raw.category.singular) ?? '',
        color: str(raw.category.color),
      }
    : null;
  return {
    place,
    category,
    radiusKm: num(raw?.radiusKm),
    total: num(raw?.total) ?? 0,
    counts,
    inCityCount: num(raw?.inCityCount),
    aroundCount: num(raw?.aroundCount),
    indexable: raw?.indexable === true,
    title: str(raw?.title) ?? t('web.service.places-service.darts-in', { name: place.name }),
    heading: str(raw?.heading) ?? str(raw?.title) ?? t('web.service.places-service.darts-in', { name: place.name }),
    description: str(raw?.description) ?? '',
    intro: str(raw?.intro) ?? '',
    guide: raw?.guide && str(raw.guide.heading) && Array.isArray(raw.guide.paragraphs)
      ? { heading: str(raw.guide.heading)!, paragraphs: raw.guide.paragraphs.map(str).filter((x: string | null): x is string => x != null) }
      : null,
    groups,
    entries: arr(raw?.entries, parsePlaceEntry),
    districtGroups: arr(raw?.districtGroups, (g) => (str(g?.name) && Array.isArray(g?.entryIds)
      ? { name: str(g.name)!, count: num(g.count) ?? g.entryIds.length, entryIds: g.entryIds.map(str).filter((x: string | null): x is string => x != null) }
      : null)),
    dataAsOf: str(raw?.dataAsOf),
    faq: arr(raw?.faq, (f) => (str(f?.question) && str(f?.answer) ? { question: str(f.question)!, answer: str(f.answer)! } : null)),
    nearbyPlaces: arr(raw?.nearbyPlaces, parsePlaceLink),
    regionCities: arr(raw?.regionCities, parsePlaceLink),
    regions: arr(raw?.regions, parsePlaceLink),
    categoryLinks: arr(raw?.categoryLinks, (c) =>
      isCategoryKey(c?.key) && str(c?.path)
        ? { key: c.key, label: str(c.label) ?? '', path: str(c.path)!, count: num(c.count) ?? 0, color: str(c.color) }
        : null),
    breadcrumbs: arr(raw?.breadcrumbs, (b) => (str(b?.name) && str(b?.path) ? { name: str(b.name)!, path: str(b.path)! } : null)),
    canonicalPath: str(raw?.canonicalPath) ?? `/darten-in/${place.slug}`,
  };
};

export const parsePlacesIndex = (raw: any): PlacesIndex => ({
  totalPlaces: num(raw?.totalPlaces) ?? 0,
  indexablePlaces: num(raw?.indexablePlaces) ?? 0,
  totalListings: num(raw?.totalListings) ?? 0,
  countries: arr(raw?.countries, (c) => {
    const name = str(c?.name);
    const slug = str(c?.slug);
    if (!name || !slug) return null;
    return {
      name,
      slug,
      cc: str(c.cc),
      path: str(c.path) ?? `/darten-in/${slug}`,
      regions: arr(c.regions, (r) => {
        const link = parsePlaceLink(r);
        return link ? { ...link, cityCount: num(r.cityCount) ?? 0, cities: arr(r.cities, parsePlaceLink) } : null;
      }),
    };
  }),
  topCities: arr(raw?.topCities, parsePlaceLink),
  canonicalPath: str(raw?.canonicalPath) ?? '/darten-in',
});

// ---------- Anzeige-Helfer ----------

/** Distanz-Format wie „In der Nähe“: "850 m", "6,9 km", "42 km" */
export const formatPlaceDistance = (km: number | null): string => {
  if (km === null || !isFinite(km) || km < 0) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(km)} km`;
};

/** "5.7.2026" für Turnier-Termine */
export const formatEntryDate = (ms: number | null): string | null => {
  if (ms === null || !isFinite(ms)) return null;
  const d = new Date(ms);
  if (isNaN(d.getTime())) return null;
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};

/** Adresse ohne Land-Suffix ("…, Deutschland") für kompakte Listen */
export const shortAddress = (label: string | null): string | null => {
  if (!label) return null;
  return label.replace(/,\s*(Deutschland|Österreich|Schweiz|Germany|Austria|Switzerland)\s*$/i, '').trim() || null;
};

export const entryThumbnail = (entry: PlaceEntry): string | null =>
  tournamentService.buildThumbnailUrl(entry.image, 200);

const WEEKDAYS = [t('web.component.opening-hours-editor.montag'), t('web.component.opening-hours-editor.dienstag'), t('web.component.opening-hours-editor.mittwoch'), t('web.component.opening-hours-editor.donnerstag'), t('web.component.opening-hours-editor.freitag'), t('web.component.opening-hours-editor.samstag'), t('web.component.opening-hours-editor.sonntag')];
const formatEuro = (v: number) => `${Number.isInteger(v) ? v : v.toFixed(2).replace('.', ',')} €`;

/** Kurze Fakten-Chips (nur echte Werte) — identisch zur Backend-Logik factLabels */
export const factLabels = (entry: Pick<PlaceEntry, 'facts'> | null | undefined): string[] => {
  const f = entry?.facts;
  if (!f) return [];
  const out: string[] = [];
  if (f.steeldart === true) out.push('Steeldart');
  if (f.edart === true) out.push('E-Dart');
  if (f.openTraining === true) out.push('Offenes Training');
  if (f.leagues !== null && f.leagues > 0) out.push(f.leagues === 1 ? '1 Liga' : `${f.leagues} Ligen`);
  if (f.membershipInfo === true) out.push('Mitgliedschaftsinfos');
  if (f.localDartTesting === true) out.push('Probewerfen');
  if (f.localPointChange === true) out.push('Point-Wechsel');
  if (f.onlineShop === true) out.push('Online-Shop');
  if (f.rentable === true) out.push('Mietbar');
  if (f.boards !== null && f.boards > 0) out.push(`${f.boards} Boards`);
  if (f.entryFee !== null) out.push(f.entryFee === 0 ? 'Startgeld frei' : `Startgeld ${formatEuro(f.entryFee)}`);
  if (f.gameMode) out.push(f.gameMode);
  if (f.startTime) out.push(`ab ${f.startTime} Uhr`);
  if (f.weekday !== null && Number.isInteger(f.weekday) && WEEKDAYS[f.weekday]) {
    out.push(f.intervalWeeks !== null && f.intervalWeeks > 1 ? `alle ${f.intervalWeeks} Wochen (${WEEKDAYS[f.weekday]})` : `jeden ${WEEKDAYS[f.weekday]}`);
  }
  return out;
};

// ---------- API ----------

const placesService = {
  async getIndex(): Promise<PlacesIndex> {
    const response = await api.get('/api/places');
    return parsePlacesIndex(response.data);
  },

  /** null = Ort unbekannt (404) */
  async getPage(slug: string, category?: PlaceCategoryKey | null): Promise<PlacePage | null> {
    try {
      const response = await api.get(`/api/places/${encodeURIComponent(slug)}`, {
        params: category ? { category } : undefined,
      });
      return parsePlacePage(response.data);
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },

  /** Top-Orte für Startseite/Footer; Fehler → [] (Sektionen blenden sich aus) */
  async getTopPlaces(limit = 20): Promise<TopPlace[]> {
    try {
      const safeLimit = Number.isInteger(limit) && limit > 0 ? Math.min(limit, 100) : 20;
      const response = await api.get('/api/places/top', { params: { limit: safeLimit } });
      return parseTopPlaces(response.data).slice(0, safeLimit);
    } catch {
      return [];
    }
  },
};

export const getTopPlaces = placesService.getTopPlaces;

export default placesService;
